import { Skeleton } from "@/components/ui/skeleton";
import type { Token } from "@/interfaces/token";
import { cn } from "@/lib/utils";
import { useState } from "react";

interface TokenIconProps {
  token: Token;
  /**
   * Unique value used to distinguish this icon
   * from other icons rendered on the same page.
   */
  id?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  showBlockchainBadge?: boolean;
  fallbackSrc?: string;
}

const sizeClasses = {
  sm: {
    container: "h-9 w-9",
    icon: "h-9 w-9",
    badge: "h-4 w-4",
    badgePosition: "-bottom-0.5 -right-0.5",
    badgeBorder: "border",
  },

  md: {
    container: "h-10 w-10",
    icon: "h-10 w-10",
    badge: "h-4.5 w-4.5",
    badgePosition: "-bottom-1 -right-1",
    badgeBorder: "border-2",
  },

  lg: {
    container: "h-12 w-12",
    icon: "h-12 w-12",
    badge: "h-5 w-5",
    badgePosition: "-bottom-1 -right-1",
    badgeBorder: "border-2",
  },
};

export const TokenIcon = ({
  token,
  id,
  size = "md",
  className,
  showBlockchainBadge = true,
  fallbackSrc = "/images/placeholder.png",
}: TokenIconProps) => {
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  const sizes = sizeClasses[size];

  const tokenName = token.name;

  const blockchainNetwork = token.blockchainNetwork;

  const blockchainIcon = blockchainNetwork?.icon;

  const iso2 = token.iso2;

  const mainIcon = iso2
    ? `https://flagcdn.com/w80/${iso2.toLowerCase()}.png`
    : (token.icon ?? undefined);

  const imageKey = id ?? token.id;

  const mainImageKey = `main-${imageKey}`;
  const badgeImageKey = `badge-${imageKey}`;

  const markImageLoaded = (key: string) => {
    setLoadedImages((prev) => ({
      ...prev,
      [key]: true,
    }));
  };

  const handleImageError = (
    key: string,
    event: React.SyntheticEvent<HTMLImageElement>,
  ) => {
    event.currentTarget.src = fallbackSrc;

    markImageLoaded(key);
  };

  return (
    <div className={cn("relative shrink-0", sizes.container, className)}>
      {/* Main image skeleton */}
      {!loadedImages[mainImageKey] && (
        <Skeleton className={cn("absolute inset-0 rounded-full", sizes.icon)} />
      )}

      {/* Main token / fiat image */}
      {mainIcon && (
        <img
          src={mainIcon}
          alt={tokenName}
          className={cn(
            "rounded-full object-cover transition-opacity duration-300",
            sizes.icon,
            loadedImages[mainImageKey] ? "opacity-100" : "opacity-0",
          )}
          onLoad={() => markImageLoaded(mainImageKey)}
          onError={(event) => handleImageError(mainImageKey, event)}
        />
      )}

      {/* Blockchain badge */}
      {showBlockchainBadge && !iso2 && blockchainIcon && (
        <>
          {!loadedImages[badgeImageKey] && (
            <Skeleton
              className={cn(
                "absolute rounded-full",
                sizes.badgePosition,
                sizes.badge,
              )}
            />
          )}

          <img
            src={blockchainIcon}
            alt={blockchainNetwork?.name ?? "Blockchain"}
            className={cn(
              "absolute rounded-full",
              sizes.badgePosition,
              sizes.badge,
              sizes.badgeBorder,
              "border-background bg-background object-cover",
              "transition-opacity duration-300",
              loadedImages[badgeImageKey] ? "opacity-100" : "opacity-0",
            )}
            onLoad={() => markImageLoaded(badgeImageKey)}
            onError={(event) => handleImageError(badgeImageKey, event)}
          />
        </>
      )}
    </div>
  );
};

export default TokenIcon;
